import { translate } from "../utilities/translation/translate";
import { upperWords } from "../utilities/string-utils";
/**
 * Shared ARIA labels object.
 * Co-locates all labels for:
 * - Future localization (i18n) via context
 * - Potential authoring customization via AppConfig
 * - Consistency across components
 *
 * All accessibility strings should flow through these functions for consistent localization.
 */
const ariaLabels = {
    // Landmark regions
    header: "CLUE Header",
    get resourcesPane() { return translate("resourcesPanel"); },
    get workspacePane() { return upperWords(translate("workspace")); },
    documentTiles: "Document tiles",
    // Navigation
    get skipToResources() { return `Skip to ${translate("resourcesPanel")}`; },
    get skipToWorkspace() { return `Skip to ${upperWords(translate("workspace"))}`; },
    skipToDashboard: "Skip to Dashboard",
    resourceTabs: "Resource navigation",
    // Dynamic label functions
    tabPanel: (tabName) => `${tabName} content`,
    tile: (tileType) => `${tileType} tile`,
    chat: (expanded) => expanded ? "Collapse chat" : "Expand chat",
    // Main workspace toolbars
    lessonToolbar: "Lesson toolbar",
    get workspaceToolbar() { return `${upperWords(translate("workspace"))} toolbar`; },
    toolbarUpper: "Tile tools",
    toolbarLower: "Playback controls",
    // Tile toolbar
    tileToolbar: "Tile toolbar",
    // Chat and resources panel
    openChatPanel: "Open chat panel",
    closeChatPanel: "Close chat panel",
    closeResourcesPanel: "Close resources panel",
    // Live region
    announcements: "Status announcements",
    // Screen reader announcements (for aria-live regions)
    // These MUST be co-located with labels for consistent i18n
    announce: {
        editingTile: (tileType) => `Editing ${tileType} tile. Press Escape to exit.`,
        exitedTile: (tileType) => `Exited ${tileType} tile. Use arrow keys to navigate.`,
        tileSelected: (tileType) => `${tileType} tile selected`,
        tileAdded: (tileType) => `${tileType} tile added`,
        tileRemoved: (tileType) => `${tileType} tile removed`,
        panelSelected: (panelName) => `${panelName} panel selected`,
    },
};
/**
 * Hook for function components.
 * Future: could pull from i18n context or AppConfig.
 */
export function useAriaLabels() {
    // Future: could pull from i18n context or AppConfig
    // const { locale } = useI18n();
    // const { ariaOverrides } = useAppConfig();
    return ariaLabels;
}
/**
 * Plain function for class components that can't use hooks.
 * Returns the same labels as useAriaLabels().
 */
export function getAriaLabels() {
    return ariaLabels;
}
//# sourceMappingURL=data:application/json;base64,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